import {
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  IconButton,
  InputAdornment,
  TextField,
  Tooltip,
} from "@material-ui/core";

import SearchIcon from "@material-ui/icons/Search";
import ClearIcon from "@material-ui/icons/Clear";

import TableBody from "./TableBody";
import TableHeader from "./TableHeader";
import CustomTablePagination from "./TablePagination";

import type {
  CustomPaginatedTableProps,
} from "./types";

const CustomPaginatedTable = <T,>({
  columns,
  data,
  totalCount,
  query,
  onQueryChange,
  rowKey,

  isLoading = false,

  search = true,
  searchPlaceholder = "Search",
  searchDebounceMs = 400,

  selection = false,
  selectedRows = [],
  onSelectionChange,
  isRowSelectable = () => true,

  selectedActionBar,
  toolbarActions,

  title,
  showTitle = false,

  emptyMessage = "No records to display.",

  pageSizeOptions = [
    10,
    15,
    20,
    30,
    100,
  ],

  headerStyle,
  rowStyle,
}: CustomPaginatedTableProps<T>) => {
  const [
    searchValue,
    setSearchValue,
  ] = useState(
    query.search ?? ""
  );

  useEffect(() => {
    setSearchValue(
      query.search ?? ""
    );
  }, [query.search]);

  useEffect(() => {
    const timer =
      window.setTimeout(() => {
        if (
          (query.search ?? "") ===
          searchValue
        ) {
          return;
        }

        onQueryChange({
          ...query,
            
          pageNumber: 1,

          search:
            searchValue,
        });
      }, searchDebounceMs);

    return () => {
      window.clearTimeout(
        timer
      );
    };
  }, [
    searchValue,
    searchDebounceMs,
    query,
    onQueryChange,
  ]);

  const selectedKeySet =
    useMemo(
      () =>
        new Set(
          selectedRows.map(
            (row) =>
              rowKey(row)
          )
        ),
      [
        selectedRows,
        rowKey,
      ]
    );

  const selectableRowsOnPage =
    useMemo(
      () =>
        data.filter(
          isRowSelectable
        ),
      [
        data,
        isRowSelectable,
      ]
        );

  const selectedRowsOnPage =
    selectableRowsOnPage.filter(
      (row) =>
        selectedKeySet.has(
          rowKey(row)
        )
    );

  const allCurrentPageSelected =
    selectableRowsOnPage.length >
      0 &&
    selectedRowsOnPage.length ===
      selectableRowsOnPage.length;

  const someCurrentPageSelected =
    selectedRowsOnPage.length >
    0;

  const handleToggleRow = (
    row: T
  ) => {
    if (
      !onSelectionChange ||
      !isRowSelectable(row)
    ) {
      return;
    }

    const key =
      rowKey(row);

    if (
      selectedKeySet.has(
        key
      )
    ) {
      onSelectionChange(
        selectedRows.filter(
          (
            selectedRow
          ) =>
            rowKey(
              selectedRow
            ) !== key
        )
      );

      return;
    }

    onSelectionChange([
      ...selectedRows,
      row,
    ]);
  };


  const handleToggleSelectAll =
    () => {
      if (
        !onSelectionChange
      ) {
        return;
      }

      const pageKeySet =
        new Set(
          selectableRowsOnPage.map(
            (row) =>
              rowKey(row)
          )
        );

      const selectionsFromOtherPages =
        selectedRows.filter(
          (row) =>
            !pageKeySet.has(
              rowKey(row)
            )
        );

      if (
        allCurrentPageSelected
      ) {
        onSelectionChange(
          selectionsFromOtherPages
        );

        return;
      }

      onSelectionChange([
        ...selectionsFromOtherPages,
        ...selectableRowsOnPage,
      ]);
    };

  return (
    <div className="w-full bg-white">
      {(search ||
        showTitle ||
        toolbarActions) && (
        <div
          className="flex items-center justify-between"
          style={{
            minHeight: 64,
            height: 64,
            paddingLeft: 0,
            paddingRight: 8,
          }}
        >
          <div className="flex items-center min-w-0">
            {showTitle && title && (
            <div
              style={{
                fontSize: "20px",
                fontWeight: 500,
                color: "rgba(0, 0, 0, 0.87)",
                marginLeft: 24,
                marginRight: 24,
              }}
            >
              {title}
            </div>
          )}

            {search && (
              <div
                style={{
                  height: 64,
                  display: "flex",
                  alignItems: "center",
                  paddingLeft: showTitle && title ? 0 : 24,
                }}
              >
                <TextField
                  value={searchValue}
                  onChange={(event) => {setSearchValue(event.target.value);}}
                  placeholder={searchPlaceholder}
                  inputProps={{"aria-label":searchPlaceholder,}}
                  InputProps={{
                    startAdornment:
                      (
                        <InputAdornment position="start">
                          <Tooltip title="Search">
                            <SearchIcon
                              fontSize="small"
                              style={{
                                color:
                                  "rgba(0, 0, 0, 0.80)",
                              }}
                            />
                          </Tooltip>
                        </InputAdornment>
                      ),
                    endAdornment:
                      (
                        <InputAdornment position="end">
                          <Tooltip title="Clear">
                            <span>
                              <IconButton
                                aria-label="clear search"
                                disabled={!searchValue}
                                onClick={() => {setSearchValue("");}}
                              >
                                <ClearIcon
                                  fontSize="small"
                                  style={{color: searchValue ? "rgba(0, 0, 0, 0.54)" : undefined,}}
                                />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </InputAdornment>
                      ),
                  }}
                />
              </div>
            )}
          </div>

          {toolbarActions && <div className="flex items-center">{toolbarActions}</div>}
        </div>
      )}

      {selectedActionBar && (
        <div style={{ padding: "0 16px" }}>{selectedActionBar}</div>
      )}
            
      <div className="w-full overflow-x-auto">
        <table
          className="w-full"
          style={{
            width:"100%",
            borderCollapse:"collapse",
            borderSpacing: 0,
            tableLayout:"auto",
          }}
        >
          <TableHeader
            columns={columns}
            query={query}
            onQueryChange={onQueryChange}
            selection={selection}
            allCurrentPageSelected={allCurrentPageSelected}
            someCurrentPageSelected={someCurrentPageSelected}
            hasSelectableRows={selectableRowsOnPage.length > 0}
            onToggleSelectAll={handleToggleSelectAll}
            headerStyle={headerStyle}
          />

          <TableBody
            columns={columns}
            data={data}
            rowKey={rowKey}
            selection={selection}
            selectedKeySet={selectedKeySet}
            onToggleRow={handleToggleRow}
            isRowSelectable={isRowSelectable}
            isLoading={isLoading}
            emptyMessage={emptyMessage}
            rowStyle={rowStyle}
          />
        </table>
          </div>

      <CustomTablePagination
        totalCount={totalCount}
        query={query}
        onQueryChange={ onQueryChange}
        pageSizeOptions={ pageSizeOptions}
      />
    </div>
  );
};

export default CustomPaginatedTable;